/**
 * Copy text to the clipboard, with a fallback for browsers that lack the async
 * Clipboard API.
 *
 * `navigator.clipboard` was only added in Safari 13.1 (and is unavailable in non-secure
 * contexts). On Safari 11.1/12.1 (macOS High Sierra/Mojave) `navigator.clipboard` is
 * `undefined`, so `navigator.clipboard.writeText(...)` throws a *synchronous* TypeError —
 * which a trailing `.catch()` cannot catch — silently breaking every "Share"/"Copy link"
 * button. This helper feature-detects the API and falls back to the legacy
 * `document.execCommand('copy')` path (a temporary off-screen textarea) so copy works
 * everywhere.
 *
 * @param {string} text - the text to copy
 * @returns {Promise<void>} resolves when copied, rejects on failure
 */
export const copyToClipboard = text => {
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    return navigator.clipboard.writeText(text);
  }

  return new Promise((resolve, reject) => {
    try {
      if (typeof document === 'undefined') {
        reject(new Error('Clipboard unavailable'));
        return;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      // Keep it out of view and non-interactive, but selectable.
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);

      // Preserve any existing selection so we can restore it afterwards.
      const selection = document.getSelection();
      const savedRange =
        selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);
      const succeeded = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (savedRange && selection) {
        selection.removeAllRanges();
        selection.addRange(savedRange);
      }

      if (succeeded) {
        resolve();
      } else {
        reject(new Error('Copy command was unsuccessful'));
      }
    } catch (e) {
      reject(e);
    }
  });
};
